import React, { useEffect } from 'react'
import type { ComponentConfig } from '@puckeditor/core'
import {
  FileUploadField,
  UploadedFile,
  getPuckFileUrl,
} from '../../fields/FileUploadField'
import { renderFontTag, useFontRegistry } from '../../fonts/FontRegistryContext'
import { fontWeightOptions } from '../../fields/StyleFields'

export type FontProps = {
  name: string
  source: 'upload' | 'url' | 'stylesheet'
  file?: UploadedFile
  url?: string
  stylesheetUrl?: string
  weight?: string
  style?: 'normal' | 'italic'
}

const FONT_ACCEPT = {
  'font/woff2': ['.woff2'],
  'font/woff': ['.woff'],
  'font/ttf': ['.ttf'],
  'font/otf': ['.otf'],
  'application/octet-stream': ['.woff2', '.woff', '.ttf', '.otf'],
}

const FontRenderer = ({
  name,
  source,
  file,
  url,
  stylesheetUrl,
  weight,
  style,
  isEditing,
}: FontProps & { isEditing: boolean }) => {
  const { register, unregister } = useFontRegistry()

  const fileUrl =
    source === 'upload' ? (file ? getPuckFileUrl(file) : '') : ''
  const directUrl = source === 'url' ? url || '' : ''
  const sheetUrl = source === 'stylesheet' ? stylesheetUrl || '' : ''
  const resolvedUrl = fileUrl || directUrl

  useEffect(() => {
    if (!name) return
    if (!resolvedUrl && !sheetUrl) return
    register({
      name,
      url: resolvedUrl || undefined,
      stylesheetUrl: sheetUrl || undefined,
      weight,
      style,
    })
    return () => unregister(name)
  }, [name, resolvedUrl, sheetUrl, weight, style, register, unregister])

  const fontEntry: any = {
    name,
    url: resolvedUrl || undefined,
    stylesheetUrl: sheetUrl || undefined,
    weight,
    style,
  }
  const tag = renderFontTag(fontEntry)

  if (!isEditing) return <>{tag}</>

  const sourceLabel =
    source === 'upload' ? 'Upload' : source === 'url' ? 'File URL' : 'Stylesheet'
  const hasSource = !!resolvedUrl || !!sheetUrl

  return (
    <>
      {tag}
      <div
        style={{
          background: '#e5e7eb',
        color: '#374151',
        border: '1px dashed #9ca3af',
        borderRadius: 4,
        padding: '8px 12px',
        font: '12px/1.4 system-ui, sans-serif',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ fontWeight: 600 }}>Font:</span>
      <span>{name || '(unnamed)'}</span>
      <span style={{ color: '#6b7280' }}>· {sourceLabel}</span>
        {!hasSource && (
          <span style={{ color: '#b91c1c', marginLeft: 'auto' }}>
            missing source
          </span>
        )}
      </div>
    </>
  )
}

export const Font: ComponentConfig<FontProps> = {
  label: 'Custom Font',
  fields: {
    name: {
      label: 'Font Family Name',
      type: 'text',
      placeholder: 'My Custom Font',
    },
    source: {
      label: 'Source',
      type: 'radio',
      options: [
        { label: 'Upload', value: 'upload' },
        { label: 'File URL', value: 'url' },
        { label: 'Stylesheet', value: 'stylesheet' },
      ],
    },
    file: {
      label: 'Font File',
      type: 'custom',
      render: ({ value, onChange }) => (
        <FileUploadField
          field={{
            type: 'fileUpload',
            label: 'Font File',
            accept: FONT_ACCEPT,
            maxSize: 10 * 1024 * 1024,
            path: '/puck/fonts/',
          }}
          value={value as UploadedFile | undefined}
          onChange={onChange}
        />
      ),
    },
    url: {
      label: 'Font URL',
      type: 'text',
      placeholder: 'https://.../font.woff2',
    },
    stylesheetUrl: {
      label: 'Stylesheet URL',
      type: 'text',
      placeholder: 'https://use.typekit.net/xxxxxxx.css',
    },
    weight: {
      label: 'Weight',
      type: 'select',
      options: fontWeightOptions,
    },
    style: {
      label: 'Style',
      type: 'radio',
      options: [
        { label: 'Normal', value: 'normal' },
        { label: 'Italic', value: 'italic' },
      ],
    },
  },
  defaultProps: {
    name: '',
    source: 'upload',
    weight: '',
    style: 'normal',
  },
  resolveFields: (data, { fields }) => {
    const next = { ...fields } as any
    const src = data.props.source
    if (src === 'upload') {
      delete next.url
      delete next.stylesheetUrl
    } else if (src === 'url') {
      delete next.file
      delete next.stylesheetUrl
    } else if (src === 'stylesheet') {
      delete next.file
      delete next.url
      delete next.weight
      delete next.style
    }
    return next
  },
  render: props => (
    <FontRenderer
      {...(props as FontProps)}
      isEditing={(props as any).puck?.isEditing}
    />
  ),
}
